import { Layers } from "@/lib/icons";
import { Badge } from "@/components/ui/badge";
import {
  breakdownConfiguration,
  type OrbitalPart,
} from "./engine";
import { type Element } from "@/data/elements";

const SUBSHELL_LABEL: Record<OrbitalPart["l"], string> = {
  s: "s (sharp)",
  p: "p (principal)",
  d: "d (diffuse)",
  f: "f (fundamental)",
};

/**
 * Answer review panel: shows the canonical electron configuration broken
 * down into the noble-gas core (if any) and each orbital after it. For
 * each orbital we show its label, electron count vs. capacity, and a
 * tiny capacity bar so learners can see how filled it is. The valence
 * shell is highlighted because it's what dominates an atom's chemistry.
 */
export default function ConfigBreakdownPanel({
  element,
  userInput,
  wasCorrect,
}: {
  element: Element;
  userInput: string | null;
  wasCorrect: boolean;
}) {
  const data = breakdownConfiguration(element);
  if (!data) return null;

  return (
    <section
      aria-label={`Configuration breakdown for ${element.name}`}
      className="mt-3 rounded-lg border border-border bg-card p-4"
    >
      <header className="flex items-center gap-2 mb-3">
        <Layers size={14} className="text-primary" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-foreground">
          Configuration breakdown
        </h3>
        <Badge variant="outline" className="text-[10px] ml-auto">
          {data.totalElectrons} electrons
        </Badge>
      </header>

      {/* Top line: full canonical answer in mono. */}
      <p className="font-mono text-sm text-foreground mb-3 break-words">
        {element.electronConfig}
      </p>

      {/* Optional noble-gas core block. */}
      {data.core && (
        <div className="mb-3 rounded-md border border-border bg-background p-3">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Noble-gas core
            </span>
            <Badge variant="secondary" className="text-[10px]">
              {data.coreElectrons} e<sup>-</sup>
            </Badge>
          </div>
          <p className="font-mono text-sm text-foreground">[{data.core}]</p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Shorthand for the {data.coreElectrons} inner electrons - identical to{" "}
            {data.core}'s full configuration.
          </p>
        </div>
      )}

      {/* Orbital list. */}
      <ul className="space-y-2">
        {data.orbitals.map((o, i) => {
          const isValence = o.n === data.valenceShell;
          const fillPct = Math.round((o.e / o.capacity) * 100);
          return (
            <li
              key={`${o.n}${o.l}-${i}`}
              className={`rounded-md border p-3 ${
                isValence
                  ? "border-primary/50 bg-primary/5"
                  : "border-border bg-background"
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-sm text-foreground">
                    {o.label}
                  </span>
                  <span className="text-[11px] text-muted-foreground truncate">
                    n = {o.n} · {SUBSHELL_LABEL[o.l]}
                  </span>
                </div>
                <span className="font-mono text-[11px] text-muted-foreground tabular-nums shrink-0">
                  {o.e} / {o.capacity}
                </span>
              </div>
              {/* Capacity bar. */}
              <div
                className="h-1.5 rounded-full bg-muted overflow-hidden"
                role="progressbar"
                aria-valuenow={o.e}
                aria-valuemin={0}
                aria-valuemax={o.capacity}
                aria-label={`${o.label}: ${o.e} of ${o.capacity} electrons`}
              >
                <div
                  className={`h-full ${isValence ? "bg-primary" : "bg-foreground/40"}`}
                  style={{ width: `${fillPct}%` }}
                />
              </div>
              {isValence && (
                <p className="text-[11px] text-primary mt-1.5">
                  Valence shell - drives this element's bonding behaviour.
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {/* Footer: compare what the player typed (only when they got it wrong). */}
      {!wasCorrect && userInput && userInput.trim().length > 0 && (
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            You typed
          </p>
          <p className="font-mono text-xs text-muted-foreground break-words">
            {userInput}
          </p>
        </div>
      )}
    </section>
  );
}